import React, { useState, useRef, useCallback } from 'react';
import api from '../api/client';
import { toast } from 'sonner';
import { Mic, Loader2 } from 'lucide-react';

/**
 * Hold-to-talk voice input button.
 * Props:
 *   onTranscript(text) — called with transcribed text
 *   className — optional extra classes
 */
export default function VoiceInput({ onTranscript, className = '' }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const activeRef = useRef(false);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      activeRef.current = true;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (!activeRef.current) return; // discarded
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) { setRecording(false); return; } // too short

        setTranscribing(true);
        try {
          const form = new FormData();
          form.append('file', blob, 'voice.webm');
          const { data } = await api.post('/api/v1/stt/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          });
          if (data.text) onTranscript(data.text);
          else toast.error('No speech detected');
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 429 && detail?.message) {
            toast.error(detail.message);
          } else {
            toast.error(typeof detail === 'string' ? detail : 'Voice transcription failed');
          }
        } finally {
          setTranscribing(false);
          setRecording(false);
        }
      };

      recorder.start(250);
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  }, [onTranscript]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Handle both mouse and touch events
  const onPointerDown = (e) => {
    e.preventDefault();
    start();
  };
  const onPointerUp = (e) => {
    e.preventDefault();
    stop();
  };

  if (transcribing) {
    return (
      <div className={`flex items-center justify-center w-9 h-9 rounded-lg bg-[#0073EA]/10 ${className}`} data-testid="voice-input-transcribing">
        <Loader2 className="w-4 h-4 animate-spin text-[#0073EA]" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onMouseDown={onPointerDown}
      onMouseUp={onPointerUp}
      onMouseLeave={recording ? onPointerUp : undefined}
      onTouchStart={onPointerDown}
      onTouchEnd={onPointerUp}
      className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all select-none ${
        recording
          ? 'bg-red-500 text-white scale-110 shadow-lg shadow-red-500/30 animate-pulse'
          : 'bg-muted/50 text-muted-foreground hover:bg-[#0073EA]/10 hover:text-[#0073EA]'
      } ${className}`}
      title={recording ? 'Release to transcribe' : 'Hold to speak'}
      data-testid="voice-input-btn"
    >
      <Mic className="w-4 h-4" />
    </button>
  );
}
